import { createApi } from '@reduxjs/toolkit/query/react';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { HttpRequest } from '@aws-sdk/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-browser';
import { getSignedRequest } from '../auth/awsCredentials';
import { getOidcToken } from '../auth/tokenManager';

const BASE_API = process.env.REACT_APP_BASE_API;
const _baseUrl = new URL(BASE_API);
const HOSTNAME = _baseUrl.hostname;
const BASE_PATH = _baseUrl.pathname.replace(/\/+$/, '');
const REGION = process.env.REACT_APP_AWS_REGION;
const IDENTITY_POOL_ID = process.env.REACT_APP_IDENTITY_POOL_ID;
const USER_POOL_ID = process.env.REACT_APP_USER_POOL_ID;

/**
 * AWS SigV4-signed baseQuery for RTK Query.
 * Automatically retrieves the idToken from the tokenManager singleton,
 * so individual endpoints never need to pass it.
 */
const awsBaseQuery = async (args, api, extraOptions) => {
  const { url, method = 'GET', body } = typeof args === 'string' ? { url: args } : args;

  const idToken = getOidcToken();
  if (!idToken) {
    return {
      error: { status: 'NO_TOKEN', message: 'Missing idToken — user may not be authenticated' },
    };
  }

  try {
    const creds = await getSignedRequest(idToken, REGION, IDENTITY_POOL_ID, USER_POOL_ID);

    const signer = new SignatureV4({
      credentials: creds,
      service: 'execute-api',
      region: REGION,
      sha256: Sha256,
    });

    const fullPath = `${BASE_PATH}${url}`;
    const urlObj = new URL(`https://${HOSTNAME}${fullPath}`);
    const queryParams = Object.fromEntries(urlObj.searchParams.entries());

    const request = new HttpRequest({
      method,
      protocol: 'https:',
      hostname: HOSTNAME,
      path: urlObj.pathname,
      query: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      headers: {
        host: HOSTNAME,
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    const signedRequest = await signer.sign(request);

    const fullUrl =
      `${signedRequest.protocol}//${signedRequest.hostname}${signedRequest.path}` +
      (signedRequest.query
        ? '?' +
          Object.entries(signedRequest.query)
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&')
        : '');

    const response = await fetch(fullUrl, {
      method: signedRequest.method,
      headers: signedRequest.headers,
      body: signedRequest.body,
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        error: {
          status: response.status,
          data,
          message: data?.detail || JSON.stringify(data),
        },
      };
    }

    return { data };
  } catch (err) {
    return { error: { status: 'FETCH_ERROR', message: err.message } };
  }
};

export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: awsBaseQuery,
  tagTypes: [
    'Releases',
    'GroupData',
    'DataGroups',
    'Untagged',
    'Schema',
    'ReleaseVersions',
    'ReleaseSchema',
    'FhirIgVersions',
    'FhirIgArtifacts',
  ],
  endpoints: () => ({}),
});
